<script setup lang="ts">
  import { FeedbackService } from '@/types/generated/services.gen'
  import {
    InformationCircleIcon,
    XMarkIcon,
    HandThumbUpIcon,
    HandThumbDownIcon
  } from '@heroicons/vue/24/outline'
  import { ref, onMounted, onUnmounted, nextTick } from 'vue'

  const props = defineProps({
    explanation: {
      type: String,
      required: true
    },
    id: {
      type: String,
      required: true
    }
  })

  const isPopupOpen = ref(false)
  const rating = ref<'positive' | 'negative' | null>(null)
  const popupRef = ref<HTMLDivElement | null>(null)
  const buttonRef = ref<HTMLButtonElement | null>(null)
  const showAbove = ref(false)

  const determinePosition = () => {
    if (!buttonRef.value || !isPopupOpen.value) return

    const buttonRect = buttonRef.value.getBoundingClientRect()
    const viewportHeight = window.innerHeight
    const buttonVerticalPosition = buttonRect.top

    // If button is in the bottom 40% of viewport, show popup above
    showAbove.value = buttonVerticalPosition > viewportHeight * 0.6
  }

  const handleInfoClick = (event: MouseEvent) => {
    event.stopPropagation()
    isPopupOpen.value = true
    nextTick(() => {
      determinePosition()
    })
  }

  const closePopup = () => {
    isPopupOpen.value = false
  }

  const rate = async (rating: string) => {
    await FeedbackService.feedbackCreate({
      requestBody: {
        rating: rating === 'up' ? 'positive' : 'negative',
        feedback_type: 'AI_ANALYSIS',
        related_uuid: props.id,
        url: window.location.href,
        feature: 'AI_ANALYSIS'
      }
    })
  }

  const handleRating = (value: 'positive' | 'negative') => {
    rating.value = value
    rate(value)
  }

  const handleClickOutside = (event: MouseEvent) => {
    if (
      isPopupOpen.value &&
      popupRef.value &&
      buttonRef.value &&
      !popupRef.value.contains(event.target as Node) &&
      !buttonRef.value.contains(event.target as Node)
    ) {
      closePopup()
    }
  }

  const handleScroll = () => {
    if (isPopupOpen.value) {
      determinePosition()
    }
  }

  onMounted(() => {
    document.addEventListener('click', handleClickOutside)
    window.addEventListener('scroll', handleScroll, { passive: true })
  })

  onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside)
    window.removeEventListener('scroll', handleScroll)
  })
</script>

<template>
  <div class="relative inline-flex">
    <button
      ref="buttonRef"
      type="button"
      class="inline-flex h-6 w-6 items-center justify-center rounded-md border border-gray-200 bg-white shadow-sm
        hover:bg-gray-50"
      @click="handleInfoClick"
    >
      <InformationCircleIcon class="h-5 w-5 text-gray-500" />
    </button>

    <Teleport to="body">
      <Transition
        enter-active-class="transition duration-200 ease-out"
        enter-from-class="transform scale-95 opacity-0"
        enter-to-class="transform scale-100 opacity-100"
        leave-active-class="transition duration-75 ease-in"
        leave-from-class="transform scale-100 opacity-100"
        leave-to-class="transform scale-95 opacity-0"
      >
        <div
          v-if="isPopupOpen"
          ref="popupRef"
          :style="{
            position: 'fixed',
            left: buttonRef?.getBoundingClientRect().right + 'px',
            top: showAbove
              ? buttonRef?.getBoundingClientRect().top - 16 - (popupRef?.offsetHeight || 0) + 'px'
              : buttonRef?.getBoundingClientRect().bottom + 16 + 'px'
          }"
          class="z-50 w-80 rounded-lg border border-gray-200 bg-white p-4 shadow-lg"
        >
          <!-- Header with close button -->
          <div class="flex items-start justify-between">
            <p class="text-sm text-gray-600">
              {{ explanation }}
            </p>
            <button
              type="button"
              class="ml-4 inline-flex items-center justify-center rounded-md text-gray-400 hover:text-gray-500"
              @click="closePopup"
            >
              <XMarkIcon class="h-5 w-5" />
            </button>
          </div>

          <!-- Rating Section -->
          <div class="mt-4 border-t border-gray-100 pt-3">
            <div class="flex items-center justify-between">
              <span class="text-sm text-gray-500">War diese Erklärung hilfreich?</span>
              <div class="flex gap-2">
                <button
                  @click="handleRating('positive')"
                  :class="[
                    'rounded-md p-1.5 transition-colors',
                    rating === 'positive'
                      ? 'bg-purple-100 text-purple-600'
                      : 'text-gray-400 hover:bg-purple-50 hover:text-purple-600'
                  ]"
                >
                  <HandThumbUpIcon class="h-5 w-5" />
                </button>
                <button
                  @click="handleRating('negative')"
                  :class="[
                    'rounded-md p-1.5 transition-colors',
                    rating === 'negative'
                      ? 'bg-purple-100 text-purple-600'
                      : 'text-gray-400 hover:bg-purple-50 hover:text-purple-600'
                  ]"
                >
                  <HandThumbDownIcon class="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>
